import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, UserContext } from '@/App';
import { ArrowLeft, Star, Clock, MapPin, Leaf, Heart, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import BottomNav from '@/components/BottomNav';

const VendorPage = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { user, addToCart, login } = useContext(UserContext);
  const [vendor, setVendor] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [showCustomization, setShowCustomization] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [customizations, setCustomizations] = useState({});
  const [menuImage, setMenuImage] = useState(null);

  useEffect(() => {
    fetchVendorDetails();
    fetchMenuItems();
    fetchReviews();
    checkFavorite();
  }, [vendorId]);

  useEffect(() => {
    // Fetch menu image if this is demo restaurant
    if (vendorId === 'demo-restaurant-001') {
      fetchMenuImage();
    }
  }, [vendorId]);

  const fetchMenuImage = async () => {
    try {
      const response = await axios.get(`${API}/demo/menu-image`);
      setMenuImage(response.data.image_url);
    } catch (error) {
      console.error('Error fetching menu image:', error);
    }
  };

  const fetchVendorDetails = async () => {
    try {
      const response = await axios.get(`${API}/vendors/${vendorId}`);
      setVendor(response.data);
    } catch (error) {
      console.error('Error fetching vendor:', error);
      toast.error('Failed to load vendor details');
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/menu-items?vendor_id=${vendorId}`);
      setMenuItems(response.data);
    } catch (error) {
      console.error('Error fetching menu items:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews?vendor_id=${vendorId}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const checkFavorite = async () => {
    if (user && user.favorites) {
      setIsFavorite(user.favorites.includes(vendorId));
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      login();
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${vendorId}`, { withCredentials: true });
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites`, { vendor_id: vendorId }, { withCredentials: true });
        toast.success('Added to favorites');
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorites');
    }
  };

  const handleAddToCart = (item) => {
    if (item.customization_options && item.customization_options.length > 0) {
      setSelectedItem(item);
      setCustomizations({ spiceLevel: 'Medium' });
      setShowCustomization(true);
    } else {
      if (vendor) {
        addToCart(item, vendor);
      }
    }
  };

  const handleCustomizedAdd = () => {
    if (selectedItem && vendor) {
      addToCart(selectedItem, vendor, customizations);
      setShowCustomization(false);
      setSelectedItem(null);
      setCustomizations({});
    }
  };

  const filteredAndSearchedItems = menuItems.filter(item => {
    const matchesFilter = 
      filter === 'all' ? true :
      filter === 'veg' ? item.is_veg :
      filter === 'non-veg' ? !item.is_veg : true;
    
    const matchesSearch = searchQuery === '' || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  if (loading || !vendor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-blue-600 font-semibold">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Header Image */}
      <div className="relative h-64 bg-gradient-to-br from-blue-400 to-blue-600">
        <div className="absolute top-4 left-4 z-10 flex gap-2">
          <button
            onClick={() => navigate(-1)}
            className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
            data-testid="back-button"
          >
            <ArrowLeft className="w-6 h-6 text-gray-800" />
          </button>
          <button
            onClick={toggleFavorite}
            className={`p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors ${
              isFavorite ? 'text-red-500' : 'text-gray-600'
            }`}
            data-testid="favorite-button"
          >
            <Heart className={`w-6 h-6 ${isFavorite ? 'fill-red-500' : ''}`} />
          </button>
        </div>
        <img
          src={vendor.image_url}
          alt={vendor.name}
          className="w-full h-full object-cover opacity-90"
        />
        {vendor.discount_percent > 0 && (
          <div className="absolute bottom-4 left-4 bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-lg shadow-lg">
            {vendor.discount_percent}% OFF
          </div>
        )}
      </div>

      {/* Vendor Info */}
      <div className="max-w-7xl mx-auto px-4 -mt-8">
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{vendor.name}</h1>
          <p className="text-gray-600 mb-4">{vendor.description}</p>
          <div className="flex items-center gap-6 text-sm flex-wrap">
            <div className="flex items-center gap-1">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <span className="font-semibold">{vendor.rating}</span>
              <span className="text-gray-500">({vendor.reviews_count} reviews)</span>
            </div>
            <div className="flex items-center gap-1 text-gray-600">
              <Clock className="w-5 h-5" />
              <span>{vendor.delivery_time}</span>
            </div>
            <div className="flex items-center gap-1 text-gray-600">
              <MapPin className="w-5 h-5" />
              <span>{vendor.location || 'Nearby'}</span>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {vendor.categories.map((cat, idx) => (
              <span key={idx} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                {cat}
              </span>
            ))}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search menu items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="menu-search"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setFilter('all')}
              className={`filter-button px-4 py-2 rounded-full font-medium border ${
                filter === 'all' ? 'active' : ''
              }`}
              data-testid="filter-all"
            >
              All
            </button>
            <button
              onClick={() => setFilter('veg')}
              className={`filter-button px-4 py-2 rounded-full font-medium border flex items-center gap-1 ${
                filter === 'veg' ? 'active' : ''
              }`}
              data-testid="filter-veg"
            >
              <Leaf className="w-4 h-4" /> Veg
            </button>
            <button
              onClick={() => setFilter('non-veg')}
              className={`filter-button px-4 py-2 rounded-full font-medium border ${
                filter === 'non-veg' ? 'active' : ''
              }`}
              data-testid="filter-nonveg"
            >
              Non-Veg
            </button>
          </div>
        </div>

        {/* Menu Image for Demo Restaurant */}
        {menuImage && vendorId === 'demo-restaurant-001' && (
          <div className="mb-6">
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <img 
                src={menuImage} 
                alt="Menu" 
                className="w-full h-auto"
              />
            </div>
          </div>
        )}

        {/* Menu Items - Zomato Style */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
          {filteredAndSearchedItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No menu items found
            </div>
          ) : (
            filteredAndSearchedItems.map((item) => (
              <div
                key={item.id}
                className="bg-white border-b border-gray-100 px-4 py-4 flex items-start gap-3 animate-fade-in last:border-b-0"
                data-testid={`menu-item-${item.id}`}
              >
                {/* Left Side - Text Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {item.is_veg ? (
                      <div className="w-5 h-5 border-2 border-green-600 flex items-center justify-center flex-shrink-0">
                        <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                      </div>
                    ) : (
                      <div className="w-5 h-5 border-2 border-red-600 flex items-center justify-center flex-shrink-0">
                        <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                      </div>
                    )}
                    <h3 className="font-bold text-gray-800 truncate">{item.name}</h3>
                  </div>
                  <p className="text-lg font-bold text-gray-800 mb-1">₹{item.price}</p>
                  <div className="flex items-center gap-1 mb-2">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <span className="text-sm text-gray-600">{item.rating}</span>
                    <span className="text-sm text-gray-400">({item.reviews_count})</span>
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-2">{item.description}</p>
                </div>

                {/* Right Side - ADD Button */}
                <div className="flex-shrink-0 flex flex-col items-center justify-center ml-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(item);
                    }}
                    className="bg-white border border-green-600 rounded-lg px-5 py-1.5 hover:bg-green-50 transition-colors"
                    data-testid={`add-to-cart-${item.id}`}
                  >
                    <span className="text-green-600 font-bold text-sm tracking-wide">ADD +</span>
                  </button>
                  {item.customization_options && item.customization_options.length > 0 && (
                    <span className="text-xs text-gray-400 mt-1">customisable</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Reviews Section */}
        {reviews.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Customer Reviews</h2>
            <div className="space-y-4">
              {reviews.slice(0, 5).map((review) => (
                <div key={review.id} className="border-b last:border-b-0 pb-4 last:pb-0">
                  <div className="flex items-center gap-3 mb-2">
                    {review.user_picture ? (
                      <img src={review.user_picture} alt={review.user_name} className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold">{review.user_name?.[0] || 'U'}</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{review.user_name || 'Anonymous'}</p>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, idx) => (
                          <Star
                            key={idx}
                            className={`w-4 h-4 ${
                              idx < review.rating
                                ? 'text-yellow-500 fill-yellow-500'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">{review.comment}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Customization Dialog */}
      <Dialog open={showCustomization} onOpenChange={setShowCustomization}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Customize Your Order</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="flex gap-3">
                <img
                  src={selectedItem.image_url}
                  alt={selectedItem.name}
                  className="w-20 h-20 rounded-lg object-cover"
                />
                <div>
                  <h3 className="font-bold">{selectedItem.name}</h3>
                  <p className="text-sm text-gray-600">₹{selectedItem.price}</p>
                </div>
              </div>

              {/* Spice Level */}
              {selectedItem.spice_levels && selectedItem.spice_levels.length > 0 && (
                <div>
                  <Label className="mb-2 block font-semibold">Spice Level</Label>
                  <RadioGroup
                    value={customizations.spiceLevel}
                    onValueChange={(value) => setCustomizations({ ...customizations, spiceLevel: value })}
                  >
                    {selectedItem.spice_levels.map((level) => (
                      <div key={level} className="flex items-center space-x-2">
                        <RadioGroupItem value={level} id={level} />
                        <Label htmlFor={level} className="cursor-pointer">{level}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}

              {/* Customization Options */}
              {selectedItem.customization_options && selectedItem.customization_options.length > 0 && (
                <div>
                  <Label className="mb-2 block font-semibold">Add-ons</Label>
                  {selectedItem.customization_options.map((option, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`addon-${idx}`}
                          checked={customizations[option.name] || false}
                          onCheckedChange={(checked) => {
                            setCustomizations({ ...customizations, [option.name]: checked });
                          }}
                        />
                        <Label htmlFor={`addon-${idx}`} className="cursor-pointer">{option.name}</Label>
                      </div>
                      <span className="text-sm text-gray-600">+₹{option.price}</span>
                    </div>
                  ))}
                </div>
              )}

              <Button
                onClick={handleCustomizedAdd}
                className="w-full btn-primary text-white"
                data-testid="add-customized"
              >
                Add to Cart
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default VendorPage;